import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MdSearch, MdArrowBack } from 'react-icons/md';
import { getGradingOverview } from '../../services/studentService';
import { getNextBelt } from '../../constants/belts';
import { beltAccent, plural, limitingRatio, StudentRow, BeltCorrectionModal } from './gradingShared';

// Page 2 of 2 — one belt's students, closest first. Reached only by tapping a rung on the
// ladder (Grading.jsx); this page is a list and is allowed to scroll.

export default function GradingBelt() {
  const { belt: beltParam } = useParams();
  const belt = decodeURIComponent(beltParam);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [correctionTarget, setCorrectionTarget] = useState(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['grading-overview'],
    queryFn: () => getGradingOverview().then(r => r.data.students),
  });

  const students = useMemo(() => (data || []).filter(s => s.currentBelt === belt), [data, belt]);
  const searchTerm = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    const list = searchTerm ? students.filter(s => s.name.toLowerCase().includes(searchTerm)) : students;
    return [...list].sort((a, b) => limitingRatio(b) - limitingRatio(a));
  }, [students, searchTerm]);

  const eligibleHere = useMemo(() => students.filter(s => s.bucket === 'eligible'), [students]);
  const nextBelt = getNextBelt(belt);

  const invalidate = () => { setCorrectionTarget(null); qc.invalidateQueries({ queryKey: ['grading-overview'] }); };

  if (isPending) return <div className="p-4 text-sm text-subtle">Loading…</div>;
  if (error) return <div className="p-4 text-sm text-red-400">Failed to load grading overview.</div>;

  return (
    <div className="p-4 pb-10">
      <Link to="/admin/grading" className="inline-flex items-center gap-1 text-xs text-subtle hover:text-secondary">
        <MdArrowBack className="text-sm" /> Back to ladder
      </Link>

      <div className="flex items-center gap-2.5 mt-3">
        <span className="w-3 h-3 rounded-full ring-1 ring-divider flex-shrink-0" style={{ background: beltAccent(belt) }} />
        <h1 className="text-xl font-semibold text-secondary">{belt} — {plural(students.length, 'student')}</h1>
      </div>

      <div className="flex items-center justify-between gap-3 mt-4">
        <div className="relative w-48">
          <MdSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-subtle text-base pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="w-full pl-8 pr-3 py-2.5 bg-card border border-divider rounded-full text-sm text-secondary placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-primary/20 min-h-[44px]"
          />
        </div>

        {eligibleHere.length > 0 && nextBelt && (
          <button
            onClick={() => navigate(`/admin/belt-exams?createFor=${encodeURIComponent(nextBelt)}`)}
            className="text-sm font-medium text-primary hover:underline min-h-[44px] flex items-center flex-shrink-0"
          >
            Schedule {nextBelt} exam
          </button>
        )}
      </div>

      <div className="mt-4 max-w-xl">
        {filtered.length === 0 ? (
          <p className="text-sm text-subtle py-3">No matches.</p>
        ) : (
          filtered.map(s => <StudentRow key={s.studentId} s={s} onCorrect={setCorrectionTarget} />)
        )}
      </div>

      {correctionTarget && (
        <BeltCorrectionModal
          student={correctionTarget}
          onClose={() => setCorrectionTarget(null)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}
